import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import projectService from '../../services/projectService';
import inspectionService from '../../services/inspectionService';
import DatePickerField from '../../components/common/DatePickerField';
import InspectionDetailModal from '../../components/common/InspectionDetailModal';
import FindingFormModal, { FINDING_STATUS_LABEL } from '../../components/common/FindingFormModal';
import FindingDetailModal from '../../components/common/FindingDetailModal';
import { NewInspectionModal } from '../inspections/Inspections';
import { formatCategory } from '../inspections/categoryOptions';
import './ProjectDetail.css';

// Client-side mirror of ComplianceService's weighting (see
// safework-api/src/services/ComplianceService.js) - used to compute the
// real per-category breakdown here, since there's no per-category backend
// endpoint yet. The overall score/band still comes straight from the
// backend via inspectionService.getScore.
const FINDING_SEVERITY_WEIGHT = { critical: 5, major: 3, minor: 1 };
const FINDING_STATUS_PROGRESS = { not_done: 0, partially_done: 50, done: 100 };
const RAG_CLASS = { GREEN: 'green', AMBER: 'amber', RED: 'red' };
const RAG_RANK = { GREEN: 0, AMBER: 1, RED: 2 };

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  // Inspections tab date filter (yyyy-mm-dd, '' means no filter)
  const [inspectionDateFilter, setInspectionDateFilter] = useState('');
  const [score, setScore] = useState(null);
  const [findings, setFindings] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [viewingInspectionId, setViewingInspectionId] = useState(null);
  // A single, shared "add inspection" entry point (see header-actions below)
  // instead of the multiple, differently-styled buttons that used to appear
  // on this page (page header + tab header + empty-state), which was
  // confusing and inconsistent with other pages' empty states.
  const [showNewInspection, setShowNewInspection] = useState(false);
  // Set when "Inspect" is clicked from a specific category row (Compliance
  // tab) so the new-inspection modal opens with that category already
  // selected instead of making the user pick it again. Null = no preset
  // (e.g. the generic header "Add Inspection" button).
  const [inspectCategory, setInspectCategory] = useState(null);
  // Risks & Issues tab - backed by the same real ComplianceFinding records
  // used everywhere else (Reports, Compliance tab), rather than a separate
  // mocked "risk" concept.
  const [showCreateFinding, setShowCreateFinding] = useState(false);
  const [editingFinding, setEditingFinding] = useState(null);
  // Separate from editingFinding - "View Details" is a read-only look at
  // what happened, distinct from "Take Action" opening the edit form.
  const [viewingFinding, setViewingFinding] = useState(null);
  const [savingFinding, setSavingFinding] = useState(false);

  // Category labels and icons
  const categoryConfig = {
    osh: { label: 'Occupational Safety & Health', icon: 'fa-hard-hat', color: '#16a34a' },
    environmental: { label: 'Environmental Compliance', icon: 'fa-leaf', color: '#0891b2' },
    labor: { label: 'Labor & Working Conditions', icon: 'fa-users', color: '#3b82f6' },
    stakeholder: { label: 'Stakeholder Engagement', icon: 'fa-comments', color: '#8b5cf6' },
    biodiversity: { label: 'Biodiversity Conservation', icon: 'fa-tree', color: '#10b981' },
    culturalHeritage: { label: 'Cultural Heritage', icon: 'fa-landmark', color: '#f59e0b' },
    gbvPrevention: { label: 'GBV & SEA Prevention', icon: 'fa-shield-alt', color: '#ef4444' },
    childLabor: { label: 'Child Labor Prevention', icon: 'fa-child', color: '#dc2626' },
    permits: { label: 'Permits & Licenses', icon: 'fa-file-contract', color: '#84cc16' },
    roadSafety: { label: 'Road Safety', icon: 'fa-car-crash', color: '#f97316' }
  };

  const fetchProject = async () => {
    try {
      setLoading(true);
      const data = await projectService.getById(id);
      setProject(data.project);
    } catch (error) {
      console.error('Failed to fetch project:', error);
      toast.error('Failed to load project');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Score + findings are refetched together (not just on mount) so the
  // Compliance tab and Risks & Issues stay in sync right after logging or
  // updating an issue, without a full page reload.
  const fetchComplianceData = useCallback(async () => {
    try {
      const [scoreData, findingsData] = await Promise.all([
        inspectionService.getScore({ projectId: id }),
        inspectionService.getFindings({ projectId: id }),
      ]);
      setScore(scoreData);
      setFindings(findingsData.findings || []);
    } catch (error) {
      console.error('Failed to load compliance data:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchComplianceData();
    (async () => {
      try {
        const inspectionsData = await inspectionService.getAll({ projectId: id });
        setInspections(inspectionsData.inspections || []);
      } catch (error) {
        console.error('Failed to load inspections:', error);
      }
    })();
  }, [id, fetchComplianceData]);

  const handleCreateFinding = async (formData) => {
    try {
      setSavingFinding(true);
      await inspectionService.createFinding({ projectId: id, ...formData });
      toast.success('Issue logged successfully');
      setShowCreateFinding(false);
      await fetchComplianceData();
    } catch (error) {
      console.error('Failed to log issue:', error);
      toast.error(error.response?.data?.error || 'Failed to log issue');
    } finally {
      setSavingFinding(false);
    }
  };

  const handleUpdateFinding = async (formData) => {
    try {
      setSavingFinding(true);
      await inspectionService.updateFinding(editingFinding.id, formData);
      toast.success('Issue updated successfully');
      setEditingFinding(null);
      await fetchComplianceData();
    } catch (error) {
      console.error('Failed to update issue:', error);
      toast.error(error.response?.data?.error || 'Failed to update issue');
    } finally {
      setSavingFinding(false);
    }
  };

  // Latest submitted Inspection per category - mirrors the backend's
  // ComplianceService.calculateProjectFindingsScore, which blends each
  // category's findings-resolution progress with its most recent inspection
  // result rather than findings alone (an old, since-fixed inspection
  // shouldn't permanently drag a category down, and a category with no
  // findings but a poor recent inspection shouldn't look falsely perfect).
  const latestInspectionByCategory = useMemo(() => {
    const map = {};
    inspections.forEach((insp) => {
      const existing = map[insp.category];
      if (!existing || new Date(insp.submittedAt) > new Date(existing.submittedAt)) {
        map[insp.category] = insp;
      }
    });
    return map;
  }, [inspections]);

  // Real per-category score, blending each category's own findings (same
  // severity-weighted calculation as the backend) with its latest inspection
  // completion percentage - the same two signals the backend's overall
  // `score` blends, just scoped down to one category at a time.
  //
  // The displayed band is still only the worst of each finding's own
  // backend-computed `rag` (ComplianceService.getFindingRag), never
  // re-derived from the blended score - that invariant is what keeps this
  // panel from ever contradicting "Findings by RAG" (e.g. showing RED while
  // 0 findings are counted as RAG-red).
  const categoryBreakdown = useMemo(() => {
    const groups = {};
    findings.forEach((f) => {
      if (!groups[f.category]) groups[f.category] = { weightedProgress: 0, maxWeighted: 0, count: 0, worstRag: 'GREEN' };
      const weight = FINDING_SEVERITY_WEIGHT[f.severity] || 1;
      const progress = FINDING_STATUS_PROGRESS[f.status] ?? 0;
      groups[f.category].weightedProgress += weight * progress;
      groups[f.category].maxWeighted += weight * 100;
      groups[f.category].count += 1;
      if ((RAG_RANK[f.rag] ?? 0) > RAG_RANK[groups[f.category].worstRag]) {
        groups[f.category].worstRag = f.rag;
      }
    });

    const categories = new Set([...Object.keys(groups), ...Object.keys(latestInspectionByCategory)]);
    const result = {};
    categories.forEach((category) => {
      const g = groups[category];
      const findingsScore = g ? g.weightedProgress / g.maxWeighted : null;
      const latestInspection = latestInspectionByCategory[category];
      const inspectionScore = latestInspection ? latestInspection.completionPercentage / 100 : null;
      const score = inspectionScore === null
        ? findingsScore
        : (findingsScore === null ? inspectionScore : (findingsScore + inspectionScore) / 2);
      result[category] = {
        score, findingsScore, inspectionScore, band: g ? g.worstRag : 'GREEN', count: g ? g.count : 0,
      };
    });
    return result;
  }, [findings, latestInspectionByCategory]);

  const getStatusBadge = (status) => {
    const badges = {
      'active': { className: 'status-success', label: 'Active', icon: 'fa-check-circle' },
      'running': { className: 'status-running', label: 'Running', icon: 'fa-play-circle' },
      'completed': { className: 'status-completed', label: 'Completed', icon: 'fa-flag-checkered' },
      'on-hold': { className: 'status-warning', label: 'On Hold', icon: 'fa-pause-circle' }
    };
    const badge = badges[status] || badges['active'];
    return (
      <span className={`status-badge ${badge.className}`}>
        <i className={`fas ${badge.icon}`}></i> {badge.label}
      </span>
    );
  };

  const getEnabledCategories = () => {
    if (!project?.trackingCategories) return [];
    return Object.entries(project.trackingCategories)
      .filter(([key, value]) => value === true)
      .map(([key]) => key);
  };

  // Categories offered when logging/editing an issue - the org's enabled
  // tracking categories plus any project-specific custom ones, so a finding
  // can only be raised against something this project actually tracks.
  const findingCategories = [
    ...getEnabledCategories().map((cat) => ({ id: cat, label: categoryConfig[cat]?.label || cat })),
    ...(project?.customCategories || []).map((cat) => ({ id: cat, label: cat })),
  ];

  if (loading) {
    return (
      <div className="project-detail-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading project...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-detail-error">
        <i className="fas fa-exclamation-triangle"></i>
        <p>Project not found</p>
        <Link to="/projects" className="btn-back">Back to Projects</Link>
      </div>
    );
  }

  return (
    <div className="project-detail-page">
      {/* Header */}
      <div className="project-detail-header">
        <div className="header-breadcrumb">
          <Link to="/projects"><i className="fas fa-arrow-left"></i> Back to Projects</Link>
        </div>
        <div className="header-main">
          <div className="header-info">
            <div className="header-title-row">
              <h1>{project.name}</h1>
              {getStatusBadge(project.status)}
            </div>
            <div className="header-meta">
              <span className="meta-item">
                <i className="fas fa-hashtag"></i> {project.code}
              </span>
              <span className="meta-item">
                <i className="fas fa-map-marker-alt"></i> {project.location}
              </span>
              {project.projectType && (
                <span className="meta-item">
                  <i className="fas fa-industry"></i> {project.projectType}
                </span>
              )}
            </div>
          </div>
          <div className="header-actions">
            <button className="btn-secondary">
              <i className="fas fa-file-pdf"></i> Generate Report
            </button>
            <button className="btn-primary" onClick={() => { setInspectCategory(null); setShowNewInspection(true); }}>
              <i className="fas fa-plus"></i> Add Inspection
            </button>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="project-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <i className="fas fa-info-circle"></i> Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'compliance' ? 'active' : ''}`}
          onClick={() => setActiveTab('compliance')}
        >
          <i className="fas fa-chart-pie"></i> Compliance
        </button>
        <button 
          className={`tab-btn ${activeTab === 'inspections' ? 'active' : ''}`}
          onClick={() => setActiveTab('inspections')}
        >
          <i className="fas fa-clipboard-check"></i> Inspections
        </button>
        <button 
          className={`tab-btn ${activeTab === 'risks' ? 'active' : ''}`}
          onClick={() => setActiveTab('risks')}
        >
          <i className="fas fa-exclamation-triangle"></i> Risks & Issues
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="overview-grid">
              {/* Project Info Card */}
              <div className="info-card">
                <h3><i className="fas fa-info-circle"></i> Project Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Project Stage</label>
                    <span className={`stage-badge ${project.projectStage}`}>
                      {project.projectStage === 'existing' ? 'Existing/Operational' : 'New/Under Construction'}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Project Type</label>
                    <span>{project.projectType || 'Not specified'}</span>
                  </div>
                  <div className="info-item">
                    <label>Location</label>
                    <span>{project.location}</span>
                  </div>
                  <div className="info-item">
                    <label>Country</label>
                    <span>{project.countryCode}</span>
                  </div>
                  {project.startDate && (
                    <div className="info-item">
                      <label>Start Date</label>
                      <span>{new Date(project.startDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  {project.endDate && (
                    <div className="info-item">
                      <label>End Date</label>
                      <span>{new Date(project.endDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  {project.contractor && (
                    <div className="info-item">
                      <label>Contractor</label>
                      <span>{project.contractor}</span>
                    </div>
                  )}
                  {project.projectManager && (
                    <div className="info-item">
                      <label>Project Manager</label>
                      <span>{project.projectManager}</span>
                    </div>
                  )}
                </div>
                {project.description && (
                  <div className="description-section">
                    <label>Description</label>
                    <p>{project.description}</p>
                  </div>
                )}
              </div>

              {/* Compliance Score Card - replaces the old manually-set
                  project.progress field, which wasn't compliance data at
                  all and showed a misleading 0% for brand-new projects. */}
              <div className="info-card">
                <h3><i className="fas fa-chart-line"></i> Progress & Status</h3>
                <div className="progress-section">
                  <div className="progress-header">
                    <span>Compliance Score</span>
                    <span className="progress-value">{score ? `${Math.round(score.score * 100)}%` : 'No data yet'}</span>
                  </div>
                  <div className="progress-bar-large">
                    <div
                      className={`progress-fill ${score ? RAG_CLASS[score.band] : ''}`}
                      style={{ width: `${score ? Math.round(score.score * 100) : 0}%` }}
                    ></div>
                  </div>
                  {score && (
                    <p className="progress-caption">
                      Blends findings resolution ({Math.round(score.findingsScore * 100)}%) with the latest
                      inspection per category ({score.inspectionScore === null ? 'no inspections yet' : `${Math.round(score.inspectionScore * 100)}%`}) - see the Compliance tab for a full breakdown.
                    </p>
                  )}
                </div>
                <div className="stats-row">
                  <div className="stat-item">
                    <div className="stat-value">{inspections.length}</div>
                    <div className="stat-label">Inspections</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value">{findings.length}</div>
                    <div className="stat-label">Findings</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value">{score?.open ?? findings.filter((f) => f.status !== 'done').length}</div>
                    <div className="stat-label">Open Issues</div>
                  </div>
                </div>
              </div>

              {/* Tracking Categories Card */}
              <div className="info-card full-width">
                <h3><i className="fas fa-check-square"></i> Tracking Categories</h3>
                <div className="categories-display">
                  {getEnabledCategories().map(cat => (
                    <div key={cat} className="category-chip">
                      <div className="chip-icon" style={{ backgroundColor: `${categoryConfig[cat]?.color}15`, color: categoryConfig[cat]?.color }}>
                        <i className={`fas ${categoryConfig[cat]?.icon}`}></i>
                      </div>
                      <span>{categoryConfig[cat]?.label || cat}</span>
                    </div>
                  ))}
                  {project.customCategories?.map((cat, idx) => (
                    <div key={`custom-${idx}`} className="category-chip">
                      <div className="chip-icon custom">
                        <i className="fas fa-tag"></i>
                      </div>
                      <span>{cat}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Map Card */}
              {project.latitude && project.longitude && (
                <div className="info-card">
                  <h3><i className="fas fa-map-marked-alt"></i> Location</h3>
                  <div className="map-embed">
                    <iframe
                      width="100%"
                      height="250"
                      frameBorder="0"
                      style={{ border: 0, borderRadius: '8px' }}
                      src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${project.latitude},${project.longitude}&zoom=14`}
                      allowFullScreen
                      title="Project Location"
                    ></iframe>
                  </div>
                  <div className="coordinates">
                    <span><i className="fas fa-location-arrow"></i> {project.latitude}, {project.longitude}</span>
                    <a 
                      href={`https://www.google.com/maps/search/?api=1&query=${project.latitude},${project.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="map-link"
                    >
                      Open in Maps <i className="fas fa-external-link-alt"></i>
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Compliance Tab */}
        {activeTab === 'compliance' && (
          <div className="compliance-tab">
            <div className="compliance-overview">
              <div className="compliance-score-card">
                <div className={`score-circle ${score ? RAG_CLASS[score.band] : ''}`}>
                  <span className="score-value">{score ? `${Math.round(score.score * 100)}%` : '—'}</span>
                  <span className="score-label">{score ? score.band : 'Loading...'}</span>
                </div>
                {score && (
                  <div className="score-breakdown">
                    <div className="score-breakdown-item">
                      <span>Findings resolution</span>
                      <strong>{Math.round(score.findingsScore * 100)}%</strong>
                    </div>
                    <div className="score-breakdown-item">
                      <span>Latest inspections</span>
                      <strong>{score.inspectionScore === null ? 'No inspections yet' : `${Math.round(score.inspectionScore * 100)}%`}</strong>
                    </div>
                  </div>
                )}
              </div>
              <div className="compliance-chart">
                <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: 600, color: '#111827' }}>Findings by RAG</h4>
                {score ? (
                  <div className="rag-mini-stats">
                    <div className="rag-mini-stat green">
                      <span className="rag-mini-count">{score.byRag.GREEN}</span>
                      <span className="rag-mini-label">Green</span>
                    </div>
                    <div className="rag-mini-stat amber">
                      <span className="rag-mini-count">{score.byRag.AMBER}</span>
                      <span className="rag-mini-label">Amber</span>
                    </div>
                    <div className="rag-mini-stat red">
                      <span className="rag-mini-count">{score.byRag.RED}</span>
                      <span className="rag-mini-label">Red</span>
                    </div>
                  </div>
                ) : (
                  <p style={{ color: '#6b7280' }}>Loading...</p>
                )}
                {score?.hasUnresolvedCritical && (
                  <p style={{ color: '#ef4444', fontSize: '13px', marginTop: '16px', marginBottom: 0 }}>
                    <i className="fas fa-exclamation-triangle"></i> This project has an unresolved Critical finding, which forces the overall rating to RED regardless of score.
                  </p>
                )}
              </div>
            </div>

            <div className="compliance-categories-detail">
              <h3>Compliance by Category</h3>
              <div className="categories-list">
                {getEnabledCategories().map(cat => {
                  const catScore = categoryBreakdown[cat];
                  // No findings and no inspection at all -> no percentage claim.
                  // Otherwise, catScore.score already blends findings-resolution
                  // with the latest inspection result for this category (see
                  // categoryBreakdown above) - never findings alone.
                  const hasData = !!catScore;
                  const pct = hasData ? Math.round(catScore.score * 100) : null;
                  const band = hasData ? catScore.band : null;
                  const statusText = hasData
                    ? (catScore.count > 0
                        ? `${catScore.count} finding${catScore.count === 1 ? '' : 's'} · ${band}`
                        : 'No findings yet')
                    : 'No inspections yet';
                  return (
                    <div key={cat} className="category-row">
                      <div className="category-info">
                        <div className="category-icon" style={{ backgroundColor: `${categoryConfig[cat]?.color}20`, color: categoryConfig[cat]?.color }}>
                          <i className={`fas ${categoryConfig[cat]?.icon}`}></i>
                        </div>
                        <div className="category-details">
                          <span className="category-name">{categoryConfig[cat]?.label || cat}</span>
                          <span className={`category-status ${band ? `band-${band.toLowerCase()}` : ''}`}>
                            {statusText}
                          </span>
                        </div>
                      </div>
                      <div className="category-progress">
                        <div className="progress-bar-sm">
                          <div className="progress-fill" style={{ width: `${pct ?? 0}%`, backgroundColor: hasData ? categoryConfig[cat]?.color : '#d1d5db' }}></div>
                        </div>
                        <span className="progress-text">{pct === null ? '—' : `${pct}%`}</span>
                      </div>
                      <button className="btn-inspect" onClick={() => { setInspectCategory(cat); setShowNewInspection(true); }}>
                        <i className="fas fa-clipboard-check"></i> Inspect
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Inspections Tab */}
        {activeTab === 'inspections' && (
          <div className="inspections-tab">
            <div className="tab-header">
              <h3>Inspection Records</h3>
            </div>

            <div className="inspections-filters">
              <select className="filter-select">
                <option value="">All Categories</option>
                {getEnabledCategories().map(cat => (
                  <option key={cat} value={cat}>{categoryConfig[cat]?.label || cat}</option>
                ))}
              </select>
              <select className="filter-select">
                <option value="">All Status</option>
                <option value="compliant">Compliant</option>
                <option value="non-compliant">Non-Compliant</option>
                <option value="needs-review">Needs Review</option>
              </select>
              <DatePickerField
                className="filter-date"
                value={inspectionDateFilter}
                onChange={setInspectionDateFilter}
                placeholder="Filter by date"
              />
            </div>

            <div className="inspections-list">
              {inspections.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-clipboard"></i>
                  <h4>No inspections recorded</h4>
                  <p>Use the "Add Inspection" button above to add your first inspection record.</p>
                </div>
              ) : (
                inspections.map(inspection => (
                  <div
                    key={inspection.id}
                    className="inspection-card"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setViewingInspectionId(inspection.id)}
                  >
                    <div className="inspection-main">
                      <div className="inspection-icon" style={{ backgroundColor: `${categoryConfig[inspection.category]?.color || '#6b7280'}20`, color: categoryConfig[inspection.category]?.color || '#6b7280' }}>
                        <i className={`fas ${categoryConfig[inspection.category]?.icon || 'fa-clipboard-check'}`}></i>
                      </div>
                      <div className="inspection-info">
                        <h4>{categoryConfig[inspection.category]?.label || formatCategory(inspection.category)}</h4>
                        <div className="inspection-meta">
                          <span><i className="fas fa-calendar"></i> {new Date(inspection.submittedAt).toLocaleDateString()}</span>
                          <span><i className="fas fa-user"></i> {inspection.inspector ? `${inspection.inspector.firstName} ${inspection.inspector.lastName}` : '—'}</span>
                          <span><i className="fas fa-percent"></i> {inspection.completionPercentage}% compliant</span>
                        </div>
                      </div>
                    </div>
                    <div className="inspection-actions">
                      <span className={`status-badge status-${inspection.overallCompliance.replace(/_/g, '-')}`}>
                        {inspection.overallCompliance.replace(/_/g, ' ')}
                      </span>
                      <button className="btn-icon" title="View Details" onClick={(e) => { e.stopPropagation(); setViewingInspectionId(inspection.id); }}>
                        <i className="fas fa-eye"></i>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Risks Tab - backed by the same real ComplianceFinding records as
            the Compliance tab and Reports, not a separate mock concept. */}
        {activeTab === 'risks' && (
          <div className="risks-tab">
            <div className="tab-header">
              <h3>Risks & Issues</h3>
              <button className="btn-primary" onClick={() => setShowCreateFinding(true)}>
                <i className="fas fa-plus"></i> Log Issue
              </button>
            </div>

            <div className="risks-summary">
              <div className="risk-stat high">
                <span className="risk-count">{findings.filter((f) => f.severity === 'critical').length}</span>
                <span className="risk-label">Critical</span>
              </div>
              <div className="risk-stat medium">
                <span className="risk-count">{findings.filter((f) => f.severity === 'major').length}</span>
                <span className="risk-label">Major</span>
              </div>
              <div className="risk-stat low">
                <span className="risk-count">{findings.filter((f) => f.severity === 'minor').length}</span>
                <span className="risk-label">Minor</span>
              </div>
            </div>

            {findings.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-check-circle"></i>
                <h4>No risks or issues logged</h4>
                <p>Use the "Log Issue" button above to raise your first one - or submit an inspection, which raises them automatically.</p>
              </div>
            ) : (
              <div className="risks-list">
                {findings.map((finding) => {
                  const severityClass = finding.severity === 'critical' ? 'high' : finding.severity === 'major' ? 'medium' : 'low';
                  return (
                    <div key={finding.id} className={`risk-card severity-${severityClass}`}>
                      <div className="risk-header">
                        <span className={`severity-badge ${severityClass}`}>{finding.severity}</span>
                        <span className={`status-pill status-${finding.status}`}>{FINDING_STATUS_LABEL[finding.status]}</span>
                      </div>
                      <h4>{finding.title}</h4>
                      <div className="risk-meta">
                        <span><i className="fas fa-tag"></i> {categoryConfig[finding.category]?.label || formatCategory(finding.category)}</span>
                        {finding.deadline && (
                          <span><i className="fas fa-calendar"></i> Due: {new Date(finding.deadline).toLocaleDateString()}</span>
                        )}
                      </div>
                      <div className="risk-actions">
                        <button className="btn-sm" onClick={() => setViewingFinding(finding)}>View Details</button>
                        <button className="btn-sm primary" onClick={() => setEditingFinding(finding)}>Take Action</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <InspectionDetailModal inspectionId={viewingInspectionId} onClose={() => setViewingInspectionId(null)} />
      {showNewInspection && (
        <NewInspectionModal
          onClose={() => { setShowNewInspection(false); setInspectCategory(null); }}
          initialProjectId={project.id}
          initialCategory={inspectCategory}
        />
      )}
      {showCreateFinding && (
        <FindingFormModal
          isOpen
          onClose={() => setShowCreateFinding(false)}
          onSave={handleCreateFinding}
          saving={savingFinding}
          categories={findingCategories}
        />
      )}
      {editingFinding && (
        <FindingFormModal
          isOpen
          onClose={() => setEditingFinding(null)}
          onSave={handleUpdateFinding}
          saving={savingFinding}
          categories={findingCategories}
          finding={editingFinding}
        />
      )}
      <FindingDetailModal
        finding={viewingFinding}
        onClose={() => setViewingFinding(null)}
        onTakeAction={() => { setEditingFinding(viewingFinding); setViewingFinding(null); }}
        onViewInspection={(inspectionId) => { setViewingInspectionId(inspectionId); setViewingFinding(null); }}
      />
    </div>
  );
};

export default ProjectDetail;
